import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { UserData } from "modules/authentication/types/user-data";
import { savePreferencesEndPoint, settingsSuscriptionsEndPoint } from "modules/main/services/endpoints";
import { useFetch } from "modules/main/hooks/useFetch";
import { forwardRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SectionHeader, SectionMiniHeader } from "modules/settings/components/section-header";
import { useMediaQuery } from "@/hooks/use-media-query";
import { isDesktopSize } from "utils/is-desktop";

/** Subscription catalog item returned by GET /settings/suscriptions. */
interface SuscriptionCatalogItem {
    id: string;
    label: string;
}

interface BuyingSettingsProps extends React.HTMLAttributes<HTMLFormElement> {
    callback?:()=>void
    update?:boolean
}

const FormSchema = z.object({
    suscriptions: z.array(z.string()).optional(),
});

export const BuyingSettings = forwardRef<HTMLFormElement, BuyingSettingsProps>(({callback,className,...props}, ref) => {
    const isDesktop = useMediaQuery(isDesktopSize);

    const { data: catalogSuscriptions, loading: loadingSuscriptions } =
        useFetch<SuscriptionCatalogItem[]>(settingsSuscriptionsEndPoint);
    const suscriptionItems = catalogSuscriptions ?? [];

    const userData = JSON.parse(localStorage.getItem("userData")??"{}") as UserData;

    const enabledSuscriptions = userData.suscriptions
                                    .filter((suscription) => suscription.enabled)
                                    .map((suscription) => suscription.suscriptionType);
    

    const form = useForm<z.infer<typeof FormSchema>>({
        resolver:zodResolver(FormSchema),
        defaultValues:{
            suscriptions:enabledSuscriptions,
        }
    });
    const handleSavePreferences = async (data: z.infer<typeof FormSchema>)=>{
        
        try{
            const response = await fetch(savePreferencesEndPoint, {
                method: "POST",
                headers:{
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data),
                credentials:'include'
            });
            if (!response.ok){
                throw new Error("Error al guardar las preferencias.");
            }

            // Update localStorage so pre-population works on next render/refresh
            const stored = JSON.parse(localStorage.getItem("userData") ?? "{}") as UserData;
            stored.suscriptions = (data.suscriptions ?? []).map((textKey) => ({
                suscriptionType: textKey,
                enabled: true,
            }));
            localStorage.setItem("userData", JSON.stringify(stored));

            toast.success("Se han guardado tus preferencias", {
                description: "Serán consisderadas en tus próximas compras.",
            });
            callback?.();

        }catch(e){
            toast.error("Error al guardar", {
                description: "No pudimos guardar tus preferencias. Intenta de nuevo.",
            });
        }
    }

    return(
    <>
    {!isDesktop&&(
    <SectionHeader
        title="Preferencias de pago"
        description="Indica tus preferencias de pago y despacho para que el optimizador las tenga en cuenta."
        className="text-sm"
    />)}
    <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSavePreferences)} ref={ref} className={cn("flex flex-col mt-2 md:mt-0 md:space-y-4",className)}  {...props}>
            {
            (isDesktop)&&(<SectionMiniHeader 
                                className=""
                                title="Preferencias de pago" 
                                description="Indica tus preferencias de pago y despacho para que el optimizador las tenga en cuenta."/>)
            }
            <div className="">
                <Label className="text:sm md:text-lg font-semibold">Suscripciones</Label>
                <p className="text-xs md:text-sm text-muted-foreground">¿Cuáles de las siguientes suscripciones deseas considerar en tus compras?</p>  
            </div>                        
            <div className="flex flex-col gap-4 my-2">               
                <FormField 
                        control={form.control}
                        name="suscriptions"
                        render={()=>{
                        return (
                        <FormItem>
                        {loadingSuscriptions ? (
                            <>
                                <Skeleton className="h-5 w-40" />
                                <Skeleton className="h-5 w-36" />
                                <Skeleton className="h-5 w-44" />
                            </>
                        ) : (
                        suscriptionItems.map((sc) => (
                            <FormField
                            key={sc.id}   
                            name="suscriptions"
                            control={form.control}
                            render={({field})=>(
                            <div className="flex items-center gap-4">
                                <FormControl>
                                    <Checkbox id={sc.id} className="h-4 w-4 mt-1"
                                        checked={field.value?.includes(sc.id)}
                                        onCheckedChange={(checked)=>{
                                            return checked 
                                                    ? field.onChange([...(field.value ?? []), sc.id])
                                                    : field.onChange(
                                                        field.value?.filter((value) => value !== sc.id)
                                                    )
                                        }}
                                    />
                                </FormControl>                                
                                <FormLabel htmlFor={sc.id} className="text-xs md:text-sm font-normal">{sc.label}</FormLabel>
                            </div>
                                )
                            }
                            />
                        ) )
                        )}
                            <FormMessage/>
                        </FormItem>
                        )}}                    
                    />                 
            </div>        
            <Button type="submit" className="mt-4">{(props.update)?"Actualizar":"¡Ir a Comprar!"}</Button>  
            
        </form>
    </Form>            
    </>
    )}
);